/**
 * API service for current action operations
 * Feature: 003-c-program-files - Send Action to Device
 */

import { auth } from '../firebase';
import { SendCurrentActionRequest, CurrentAction, CurrentActionRef } from '../types/currentAction';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Get Firebase Auth token for current user
 */
async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }
  return await user.getIdToken();
}

/** An error with the HTTP status, so a caller can tell 404 and 409 apart */
export class CurrentActionApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'CurrentActionApiError';
  }
}

/**
 * Handle API response and errors
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));

    if (response.status === 403) {
      throw new CurrentActionApiError(
        error.detail || "You don't have permission to control this device. Contact the device owner.",
        response.status
      );
    } else if (response.status === 400 || response.status === 404 || response.status === 409) {
      throw new CurrentActionApiError(error.detail || 'Invalid request', response.status);
    } else {
      throw new CurrentActionApiError('Failed to save. Please try again.', response.status);
    }
  }

  return response.json();
}

export const currentActionService = {
  /**
   * Send action to device (creates or updates current action)
   */
  async sendAction(deviceId: string, request: SendCurrentActionRequest): Promise<CurrentAction> {
    const token = await getAuthToken();

    const response = await fetch(`${API_BASE}/api/devices/${deviceId}/current-action`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    return handleResponse<CurrentAction>(response);
  },

  /**
   * Edit settings of current action
   */
  async editAction(deviceId: string, request: SendCurrentActionRequest): Promise<CurrentAction> {
    const token = await getAuthToken();

    const response = await fetch(`${API_BASE}/api/devices/${deviceId}/current-action`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    return handleResponse<CurrentAction>(response);
  },

  /**
   * Remove current action from device
   */
  async removeAction(deviceId: string): Promise<void> {
    const token = await getAuthToken();

    const response = await fetch(`${API_BASE}/api/devices/${deviceId}/current-action`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    await handleResponse<{ message: string }>(response);
  },

  /**
   * Approve the current action that waits for the parent: it ends on the tablet.
   * Rejects with status 409 if a different action is current now, 404 if none is.
   */
  async approveAction(deviceId: string, ref: CurrentActionRef): Promise<void> {
    const token = await getAuthToken();

    const response = await fetch(`${API_BASE}/api/devices/${deviceId}/current-action/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ actionId: ref.actionId, startTimestamp: ref.startTimestamp }),
    });

    await handleResponse<{ message: string }>(response);
  },
};
